import React, { useMemo, useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, MenuItem } from '@mui/material';
import {NOTIFICATION_TYPE, showNotificationBar} from '../../../actions/error-pages/NotificationBarActions';
import { LoadingButton, TextButton } from '../../common/StyledButtons';
import { withRouter } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { SUBMISSION_TYPES_HUMAN_MAP } from '../../../constants/Statuses';
import { StyledAutocomplete, StyledMultilineTextField, StyledTextField } from '../../common/StyledTextField';
import { StyledDropDown } from '../../common/StyledDropDown';
import { ADMIN_SUBMISSION_TYPES, SUBMISSION_TYPES, postSubmission } from '../../../actions/external/SubmissionActions';
import { getAllSites, getAllTeamMembers } from '../../../selectors/AdminSelectors';
import StyledSwitch from '../../common/StyledSwitch';
import { getUserId } from '../../../selectors/UserSelectors';
import { getAllSubmissionsForAdmin } from '../../../actions/internal/AdminActions';
import { CalendarMonthOutlined, NotificationsActiveOutlined } from '@mui/icons-material';
import { StyledDatePicker } from '../../common/StyledDatePicker';
import StyledDialog from '../../common/StyledDialog';
import Colors from '../../../constants/Colors';

const AdminAddRequestModal = ({ open, onClose }) => {

  const [ selectedSiteId, setSelectedSiteId ] = useState();
  const [ requestType, setRequestType ] = useState();
  const [ assignedTo, setAssignedTo ] = useState();
  const [ sendNotification, setSendNotification ] = useState(true);
  const [ values, setValues ] = useState({ title: '', description: ''})
  const [ errors, setErrors ] = useState([]);

  const [showDueDateSection, setShowDueDateSection] = useState(false);
  const [dueDate, setDueDate] = useState(null);
  const [dueDateError, setDueDateError] = useState(false);

  const [showReminderSection, setShowReminderSection] = useState(false);
  const [customReminderDate, setCustomReminderDate] = useState(null);

  const [ isLoading, setIsLoading ] = useState(false);

  const sites = getAllSites();
  const teamMembers = getAllTeamMembers();

  const setFieldValue = (id, value) => {
    setValues({ ...values, [id]: value })
  }

  const clearForm = () => {
    setSelectedSiteId();
    setRequestType();
    setAssignedTo();
    setValues({});
    setErrors([]);
    setDueDate();
    setShowDueDateSection(false);
    setDueDateError(false);
    setCustomReminderDate(undefined);
    setShowReminderSection(false);
  }

  const handleClose = () => {
    clearForm();
    onClose();
  }

  const validateFields = () => {
    const errors = Object.keys(values).filter(key => !values[key] || values[key].trim() === '');
    if (!selectedSiteId) errors.push('business');
    if (!requestType) errors.push('requestType');
    if ((showDueDateSection || showReminderSection) && !assignedTo) errors.push('assignedTo')
    if (showDueDateSection && !dueDate) errors.push('dueDate')
    if (showReminderSection && !customReminderDate) errors.push('customReminderDate')
    return errors;
  }

  const getFormattedBusinessOptions = () => {
    if (!sites) return [];
    return sites.map(site => ({
      label: site.site_details.site_name,
      id: site.site_id
    }));
  }

  const handleAddRequest = async () => {
    try {
      // validate payload
      const errors = validateFields();
      if (errors.length > 0) {
        setErrors(errors)
        return;
      }
      setIsLoading(true);
      await postSubmission(
        requestType,
        { user_id: null, site_id: selectedSiteId },
        values,
        [],
        assignedTo,
        dueDate,
        customReminderDate,
        { send_submission_notification: sendNotification }
      )
      await getAllSubmissionsForAdmin(getUserId(), true, true);
      showNotificationBar(NOTIFICATION_TYPE.SUCCESS, "This request has been created successfully!");
      clearForm();
      onClose();
    } catch(ex) {
      console.error(ex);
      showNotificationBar(NOTIFICATION_TYPE.ERROR, "An error has occurred while canceling this request. If this continues to happen, please contact us.");
    } finally {
      setIsLoading(false)
    }
  }

  const handleBusinessSelected = (event, newValue) => {
    if (!newValue) {
      setSelectedSiteId();
      return;
    }
  
    const { id } = newValue;
    setSelectedSiteId(id);
  };

  const customReminderDateWarning = useMemo(() => {
    if (!customReminderDate) return '';
  
    const selectedDate = customReminderDate.toDate
      ? customReminderDate.toDate()
      : new Date(customReminderDate);
  
    const now = new Date();
  
    if (selectedDate < now) {
      return 'Warning: This reminder date is in the past.';
    }
  
    return '';
  }, [customReminderDate]);

  // const { submission_status, submission_fields } = request || {}
  // const defaultFieldValues = submission_fields || {}
  // const isAdmin = isUserAdmin();

  return (
    <>
      <StyledDialog
          fullWidth
          open={open}
          maxWidth={'md'}
      >
          <DialogTitle>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
              <Box>
                <h4 style={{ margin: 0, padding: 0 }}>Add Request</h4>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
              <StyledAutocomplete
                label="Business*"
                options={getFormattedBusinessOptions()}
                onChange={handleBusinessSelected}
                error={errors.includes('business')}
              />

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: {
                    xs: 'column',
                    sm: 'column',
                    md: 'row',
                  },
                  gap: 2,
                  width: '100%',
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <StyledDropDown
                    label="Request Type*"
                    onChange={(e) => setRequestType(e.target.value)}
                    error={errors.includes('requestType')}
                  >
                    {Object.keys(ADMIN_SUBMISSION_TYPES).map(key => (
                      <MenuItem key={key} value={key}>
                        {SUBMISSION_TYPES_HUMAN_MAP[key]}
                      </MenuItem>
                    ))}
                  </StyledDropDown>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <StyledDropDown
                    label="Assigned To (Optional)"
                    value={assignedTo}
                    onChange={(e) => setAssignedTo(e.target.value)}
                    error={errors.includes('assignedTo')}
                    helperText={errors.includes('assignedTo') && 'Assign a teammate for notifications.'}
                  >
                    <MenuItem defaultValue key="UNASSIGNED" value="UNASSIGNED">
                      <em>Unassigned</em>
                    </MenuItem>

                    {teamMembers && teamMembers.map(member => (
                      <MenuItem key={member.user_id} value={member.user_id}>
                        {member.team_member_details.first_name} {member.team_member_details.last_name}
                      </MenuItem>
                    ))}
                  </StyledDropDown>
                </Box>
              </Box>

              <StyledTextField
                label="Request Title*"
                onChange={(e) => setFieldValue('title', e.target.value)}
                error={errors.includes('title')}
              />

              <StyledMultilineTextField
                label="Description*"
                onChange={(e) => setFieldValue('description', e.target.value)}
                error={errors.includes('description')}
              />
              <Box sx={{ marginTop: '12px' }}>
              <Box
                  sx={{
                    display: 'flex',
                    flexDirection: {
                      xs: 'column',
                      sm: 'row',
                    },
                    gap: 1.5,
                    alignItems: {
                      xs: 'flex-start',
                      sm: 'center',
                    },
                    marginTop: '8px',
                  }}
                >
                  <TextButton
                  onClick={() => setShowReminderSection(prev => !prev)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '4px',
                    color: showReminderSection || customReminderDate ? Colors.primaryColor : 'inherit',
                    '& svg': {
                      color: 'inherit',
                    },
                  }}
                >
                  <NotificationsActiveOutlined fontSize="small" sx={{ marginRight: '4px' }} />
                  {showReminderSection ? 'Remove Reminder' : 'Add Reminder'}
                </TextButton>
                <TextButton
                  onClick={() => setShowDueDateSection(prev => !prev)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '4px',
                    color: showDueDateSection || dueDate ? Colors.primaryColor : 'inherit',
                    '& svg': {
                      color: 'inherit',
                    },
                  }}
                >
                  <CalendarMonthOutlined fontSize="small" sx={{ marginRight: '4px' }} />
                  {showDueDateSection ? 'Remove Due Date' : 'Set Due Date'}
                </TextButton>
                
              </Box>
              

              {showReminderSection && (
                <Box
                  mt={2}
                  width="100%"
                  display="flex"
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  gap={2}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                >
                  <Box flex={1} width="100%">
                    <StyledDatePicker
                      fullWidth
                      label="Reminder Date"
                      views={["year", "month", "day", "hours", "minutes"]}
                      value={customReminderDate}
                      onChange={(newDate) => {
                        setCustomReminderDate(newDate);
                        setErrors([]);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          placeholder: 'Choose a reminder date',
                        },
                      }}
                    />
                    <p style={{ paddingTop: '5px', margin: 0 }}>
                      {errors.includes('customReminderDate') && 'Please select a valid date.'}
                      {customReminderDateWarning && <p>{customReminderDateWarning}</p>}
                    </p>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '8px',
                      backgroundColor: '#f7f9fc',
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      lineHeight: 1.5,
                      border: '1px solid #e6edf5',
                    }}
                  >
                    <strong>
                      Reminder Notifications:
                    </strong>{' '}
                    An email reminder will be sent to the assigned team member on the selected date.
                  </Box>
                </Box>
              )}
                {showDueDateSection && (
                  <Box
                    mt={2}
                    width="100%"
                    display="flex"
                    flexDirection={{ xs: 'column', sm: 'row' }}
                    gap={2}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                  >
                    <Box flex={1} width="100%">
                      <StyledDatePicker
                        fullWidth
                        label="Due Date"
                        views={['year', 'month', 'day']}
                        value={dueDate}
                        onChange={(newDate) => {
                          setDueDate(newDate);
                          setDueDateError(false);
                          setErrors([]);
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            placeholder: 'Choose a due date',
                          },
                        }}
                      />
                      <p style={{ paddingTop: '5px', margin: 0 }}>{errors.includes('dueDate') && 'Please select a valid date.'}</p>
                    </Box>

                    <Box
                      sx={{
                        flex: 1,
                        padding: '12px',
                        borderRadius: '8px',
                        backgroundColor: '#f7f9fc',
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        lineHeight: 1.5,
                        border: '1px solid #e6edf5',
                      }}
                    >
                      <strong>
                        Due Date Notifications:
                      </strong>{' '}
                      An email will be sent to the assigned team member 5 days before the due date and again 24 hours before the due date.
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', padding: '16px'}}>
              <Box>
                <StyledSwitch
                    title="Notify Client of New Request"
                    onChange={(e) => { setSendNotification(e.target.checked) }}
                    checked={sendNotification}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end'}}>
                <TextButton sx={{ marginRight: '10px' }} onClick={handleClose} disabled={isLoading}>Cancel</TextButton>
                <LoadingButton onClick={handleAddRequest} isLoading={isLoading}>Save</LoadingButton>
              </Box>
            </Box>
      </StyledDialog>
    </>
  )
}

export default observer(withRouter(AdminAddRequestModal));
